import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/lib/seo";
import { EASE, viewportOnce } from "@/lib/motion";
import { EXPERTISES, IMAGES } from "@/lib/data";
import { Fade, Mask } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";

export default function Expertises() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (!el) return;
      const lenis = (window as unknown as { __lenis?: { scrollTo: (t: HTMLElement, o?: object) => void } }).__lenis;
      if (lenis) lenis.scrollTo(el, { offset: -90 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 450);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <main id="contenu">
      <Seo
        title="Expertises — Sonorisation, éclairage, régie technique, scénographie | SONOCO EVENTS"
        description="Sonorisation événementielle professionnelle, éclairage scénique et architectural, régie technique, infrastructures scéniques, scénographie et fabrication : les cinq départements techniques de SONOCO EVENTS, prestataire technique événementiel à Fréjus (Var)."
        path="/expertises"
      />

      <header className="mx-auto max-w-[1600px] px-5 pb-16 pt-32 sm:px-10 sm:pb-24 sm:pt-44 lg:px-16">
        <SectionLabel num="02" name="Expertises" />
        <h1 className="mt-10 text-[clamp(3rem,10vw,9rem)] font-extrabold uppercase leading-[0.85] tracking-tightest">
          <Mask>Nos expertises.</Mask>
        </h1>
        <Fade className="mt-10 max-w-2xl" delay={0.15}>
          <p className="text-lg leading-relaxed text-mute sm:text-xl">
            Cinq départements techniques, une seule exigence : une exécution maîtrisée. Nous
            concevons, préparons et déployons chaque dispositif en fonction des lieux, des
            contraintes et des objectifs de chaque production.
          </p>
        </Fade>
      </header>

      {EXPERTISES.map((ex) => (
        <section key={ex.slug} id={ex.slug} className="scroll-mt-24 border-t border-line">
          <div className="mx-auto grid max-w-[1600px] gap-12 px-5 py-16 sm:px-10 sm:py-24 lg:grid-cols-12 lg:px-16">
            <div className="lg:sticky lg:top-28 lg:col-span-5 lg:self-start">
              <Fade>
                <span className="font-mono text-xs tracking-[0.25em] text-mute">{ex.num} /</span>
                <h2 className="mt-5 text-3xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-5xl">
                  {ex.name}
                </h2>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                  {ex.short}
                </p>
                <div className="mt-8 flex flex-wrap gap-2">
                  {ex.caps.map((c) => (
                    <span
                      key={c}
                      className="border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-mute"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </Fade>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <Fade>
                <div className="overflow-hidden border border-line">
                  <motion.img
                    src={ex.img}
                    alt={ex.alt}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover"
                    initial={{ scale: 1.08 }}
                    whileInView={{ scale: 1 }}
                    viewport={viewportOnce}
                    transition={{ duration: 1.4, ease: EASE }}
                  />
                </div>
              </Fade>
              <div className="mt-10 space-y-6">
                {ex.copy.map((p, i) => (
                  <Fade key={i} delay={i * 0.08}>
                    <p
                      className={
                        i === 0
                          ? "text-xl font-light leading-relaxed text-ink sm:text-2xl"
                          : "text-lg leading-relaxed text-mute"
                      }
                    >
                      {p}
                    </p>
                  </Fade>
                ))}
              </div>
              {ex.slug === "eclairage" && (
                <Fade className="mt-10" delay={0.1}>
                  <div className="overflow-hidden border border-line">
                    <img
                      src={IMAGES.architectural}
                      alt="Façade d'un bâtiment mise en valeur par un éclairage architectural — SONOCO EVENTS"
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover"
                    />
                  </div>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                    Éclairage architectural
                  </p>
                </Fade>
              )}
            </div>
          </div>
        </section>
      ))}

      <section className="border-t border-line bg-panel">
        <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-10 sm:py-32 lg:px-16">
          <Fade>
            <h2 className="text-[clamp(2.2rem,6vw,5rem)] font-extrabold uppercase leading-[0.9] tracking-tightest">
              Un projet technique ?
            </h2>
            <Link
              to="/contact"
              data-testid="expertises-cta-contact"
              className="group mt-8 inline-flex items-center gap-3 border border-ink/30 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-bg"
            >
              Parlons-en
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Fade>
        </div>
      </section>
    </main>
  );
}
